import React, { useEffect, useState, useContext } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { ToastContext } from '../../../services/contexts/toast-context';
import { useUploadProjectScreenshot } from '../../../services/api/projects-api';

const PreviewWrap = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`;

const DropZone = styled.div`
  border: 2px dashed ${props => props.isDragActive ? '#58a6ff' : '#cbd5e1'};
  border-radius: 16px;
  padding: 2.5rem 2rem;
  text-align: center;
  background: ${props => props.isDragActive ? 'rgba(88, 166, 255, 0.05)' : 'rgba(148, 163, 184, 0.03)'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 1.5rem;

  &:hover {
    border-color: #58a6ff;
    background: rgba(88, 166, 255, 0.08);
  }

  i {
    font-size: 2.5rem;
    color: #58a6ff;
    margin-bottom: 0.75rem;
    display: block;
  }

  p {
    margin: 0.75rem 0 0 0;
    color: var(--text-main);
    font-weight: 600;

    span {
      color: var(--text-secondary);
      font-weight: 400;
      font-size: 0.9rem;
      display: block;
      margin-top: 0.35rem;
    }
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const Controls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

export default function ScreenshotUploader({
  visible,
  onHide,
  project,
}) {
  const toastRef = useContext(ToastContext);
  const { mutateAsync: uploadScreenshot, isPending } = useUploadProjectScreenshot();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (!visible) {
      setFile(null);
      setPreview(null);
      setIsDragActive(false);
    }
  }, [visible]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    processFile(f);
  };

  const processFile = (f) => {
    if (f && f.type.startsWith('image/')) {
      setFile(f);
      const previewUrl = URL.createObjectURL(f);
      setPreview(previewUrl);
    } else if (f) {
      toastRef?.current?.show({
        severity: 'warn',
        summary: 'Invalid File',
        detail: 'Please select an image file',
        life: 3000,
      });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragActive(true);
    } else if (e.type === 'dragleave') {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    processFile(f);
  };

  const handleUpload = async () => {
    if (!file || !project) return;
    
    try {
      await uploadScreenshot({ projectId: project.id, file });
      toastRef?.current?.show({ severity: 'success', summary: 'Uploaded', detail: 'Screenshot uploaded', life: 3000 });
      onHide();
    } catch (err) {
      toastRef?.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Upload failed', life: 3000 });
    }
  };

  return (
    <Dialog
      visible={visible}
      header={`Add Screenshot${project ? ` — ${project.title}` : ''}`}
      modal
      onHide={onHide}
      style={{ width: '560px', maxWidth: '95vw' }}
    >
      <div>
        <DropZone
          isDragActive={isDragActive}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <i className="pi pi-cloud-upload" />
          <p>
            Drag and drop your screenshot here
            <span>or click to browse</span>
          </p>
        </DropZone>

        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
        />

        {preview && (
          <PreviewWrap>
            <img src={preview} alt="preview" />
          </PreviewWrap>
        )}

        <Controls>
          <Button label="Cancel" className="p-button-text p-button-secondary" onClick={onHide} />
          <Button label="Upload" className="p-button-primary" loading={isPending} disabled={!file} onClick={handleUpload} />
        </Controls>
      </div>
    </Dialog>
  );
}

ScreenshotUploader.propTypes = {
  visible: PropTypes.bool,
  onHide: PropTypes.func,
  project: PropTypes.object,
};
